import { Share } from "react-native";
import * as FileSystem from "expo-file-system/legacy";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db, COLLECTIONS } from "../config/firebase";
import { EventService } from "./eventService";
import { toCsv } from "../utils/csv";
import { toDate } from "../utils/eventHelpers";

const EVENT_BATCH_SIZE = 5;
// Name cell for an attendance row whose profile could not be loaded.
const MISSING_PROFILE = "Profile unavailable";

const pad = (n) => String(n).padStart(2, "0");

// Local "YYYY-MM-DD HH:mm"; "" when missing/invalid.
function formatStamp(value) {
  const d = toDate(value);
  if (!d) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
}

function formatDay(value) {
  return formatStamp(value).slice(0, 10);
}

function slugify(text) {
  const slug = String(text || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 50);
  return slug || "export";
}

// Current semester: Aug 1 - Dec 31, or Jan 1 - Jul 31 (computed at run time).
function getSemesterRange(now = new Date()) {
  const year = now.getFullYear();
  if (now.getMonth() >= 7) {
    return {
      start: new Date(year, 7, 1),
      end: new Date(year + 1, 0, 1),
      label: `Fall ${year}`,
    };
  }
  return {
    start: new Date(year, 0, 1),
    end: new Date(year, 7, 1),
    label: `Spring ${year}`,
  };
}

async function writeAndShare(csv, baseName, title) {
  const stamp = formatDay(new Date());
  const fileUri = `${FileSystem.cacheDirectory}${slugify(baseName)}-${stamp}.csv`;
  try {
    await FileSystem.writeAsStringAsync(fileUri, csv, { encoding: "utf8" });
    await Share.share({ url: fileUri, title });
  } finally {
    FileSystem.deleteAsync(fileUri, { idempotent: true }).catch(() => {});
  }
}

export async function exportEventAttendanceCsv(event) {
  try {
    if (!event?.id) return { data: null, error: "Missing event." };
    const { data: attendees, error } = await EventService.getEventAttendees(
      event.id,
      { includeMissingProfiles: true }
    );
    if (error) return { data: null, error: "Could not load attendees." };

    const rows = (attendees || []).map((a) => [
      a.user?.full_name || MISSING_PROFILE,
      a.user?.email || "",
      formatStamp(a.rsvp_at),
      a.checked_in ? "Yes" : "No",
      formatStamp(a.checked_in_at),
      a.checked_in ? a.check_in_method || "" : "",
    ]);
    const csv = toCsv(
      ["Name", "Email", "RSVP at", "Checked in", "Checked in at", "Check-in method"],
      rows
    );
    await writeAndShare(csv, event.title || "event", `${event.title || "Event"} attendance`);
    return { data: { rows: rows.length }, error: null };
  } catch (error) {
    if (__DEV__) console.error("Export event attendance error:", error);
    return { data: null, error: "Could not export attendance." };
  }
}

export async function exportClubSemesterCsv(clubId, clubName) {
  try {
    if (!clubId) return { data: null, error: "Missing club." };
    const { start, end, label } = getSemesterRange();
    const snapshot = await getDocs(
      query(collection(db, COLLECTIONS.EVENTS), where("club_id", "==", clubId))
    );
    const events = snapshot.docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((e) => {
        if (e.is_active === false) return false; // deleted events
        const s = toDate(e.start_date);
        return s && s >= start && s < end;
      })
      .sort((a, b) => toDate(a.start_date) - toDate(b.start_date));

    // people keyed by user id; statusByEvent maps event index -> status text
    const people = new Map();
    for (let i = 0; i < events.length; i += EVENT_BATCH_SIZE) {
      const batch = events.slice(i, i + EVENT_BATCH_SIZE);
      const results = await Promise.all(
        batch.map((e) => EventService.getEventAttendees(e.id, { includeMissingProfiles: true }))
      );
      for (let j = 0; j < results.length; j += 1) {
        const { data, error } = results[j];
        if (error) return { data: null, error: "Could not load attendees." };
        for (const a of data || []) {
          const key = a.user?.id || a.user_id;
          if (!key) continue;
          const existing = people.get(key);
          if (!existing) {
            people.set(key, {
              name: a.user?.full_name || MISSING_PROFILE,
              email: a.user?.email || "",
              byEvent: {},
            });
          } else if (existing.name === MISSING_PROFILE && a.user?.full_name) {
            // A later event loaded the profile an earlier one couldn't.
            existing.name = a.user.full_name;
            existing.email = a.user.email || existing.email;
          }
          people.get(key).byEvent[i + j] = a.checked_in ? "Attended" : "RSVP";
        }
      }
    }

    const eventHeaders = events.map(
      (e) => `${e.title || "Event"} (${formatDay(e.start_date)})`
    );
    const rows = [...people.values()].map((p) => {
      const statuses = events.map((_, idx) => p.byEvent[idx] || "");
      return [
        p.name,
        p.email,
        statuses.filter(Boolean).length,
        statuses.filter((s) => s === "Attended").length,
        ...statuses,
      ];
    });
    const csv = toCsv(
      ["Name", "Email", "Events RSVPed", "Events attended", ...eventHeaders],
      rows
    );
    const name = `${clubName || "club"} ${label}`;
    await writeAndShare(csv, name, `${name} attendance`);
    return { data: { rows: rows.length }, error: null };
  } catch (error) {
    if (__DEV__) console.error("Export semester attendance error:", error);
    return { data: null, error: "Could not export attendance." };
  }
}
